import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { Place } from './place';

import { PriceZoneView, PriceZoneViewProxy } from '../views/pricezone';

import { QrCode } from './qrcode';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { I18nValueType } from '../types';

const _schema = {
    name: 'PRICEZONE',
    fields: [
        { name: 'status', type: 'string', default: "'AC'" },
        { name: 'place', type: 'objid' },
        { name: 'name', type: 'objid' },
        { name: 'sort', type: 'number' },
    ],
    relate: [
        { direction: '>', target: 'PLACE', by: 'place', name: 'place', reason: 'place', child: false, class: PlaceType },
        { direction: '>', target: 'I18NVALUE', by: 'name', name: 'name', reason: 'name', child: true, class: I18nValueType },
    ]
} as const;

const _PriceZoneClass = CreateObject(_schema);
if (!_PriceZoneClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class PriceZone extends _PriceZoneClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): PriceZone {
        return this._Copy(store) as PriceZone;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddPricezone(this);
            }
        }
    }

    override get viewProxy(): PriceZoneView {
        return PriceZoneViewProxy(this, this.data);
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    get status(): string | null {
        return this['__base__status'];
    }

    /**
     * When status changes, notify the owning place:
     * - new zone (not yet saved): remove from place.pricezones on DE
     * - persisted zone: keep the row, clear `qrcode.pricezone` on tables pointing to it
     *   (they fall back to the default zone) and refresh PriceAreas cache on DE
     */
    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        const _place = this.place;
        if (!_place){
            return;
        }

        if (this.status == 'DE'){
            if (!this.Commited){
                _place.DelPricezone(this);
                return;
            }

            for (const _qrcode of this.qrcodes){
                _qrcode.pricezone = null;
            }
        }

        _place.DoRefresh(this.table);
    }

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    /** Valid QRCODE rows assigned to this price zone (derived from `place.qrcodes` via `QRCODE.pricezone`). */
    get qrcodes(): QrCode[] {
        const _place = this.place;
        if (!_place){
            return [];
        }

        const _qrcodes: QrCode[] = [];
        for (const _qrcode of _place.qrcodes ?? []){
            if (!_qrcode.IsValid){
                continue;
            }

            if (_qrcode.pricezone === this){
                _qrcodes.push(_qrcode);
            }
        }

        return _qrcodes;
    }

}
